import React, { useCallback, useEffect, useState } from "react";
import {Box, Card, CardActionArea, CardContent, Chip, Typography, Button, Dialog, Grid, Tooltip} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import { useHistory } from "react-router-dom";
import useStyles from "../../assets/styles/HomeStyles";
import AccessInfoIcon from "../../assets/images/DQProImages/assess-info-icon.png"
import ProgramInfoIcon from "../../assets/images/DQProImages/product-info-icon.png"
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useAuth } from "../../context/auth";
import { useColorMode } from "../../context/colorMode/colorMode.context";
import { USER_ACCESS_LEVEL, PRODUCT_KEY, TOAST_TYPES, FEATURE_KEY, DQ_PRO_PLAN_KEY } from "../../utils/constants";
import {
    clearChatStateInsiderService,
    getActiveChatInsiderProgramService,
} from "../../services/learning_programs.service";
import { activateProductService } from "../../services/product.service";
import { listProgramRequestsService, markSchoolDeliveriesSeenService, getConsentSetupService } from "../../services/client.service";
import { getUserDetailsService, updateUserDetailsService } from "../../services/auth.service";
import { toast } from "../../utils/utils";
import { isProductEnabled, isFeatureEnabled, solutionPlanKey, canCreateProduct } from "../../utils/productAccess";
import { isTeacher, isLcTeacher, isClientWorkspace } from "../../utils/capabilities";
import { useSituationalTutorials } from "../../components/TutorialSystem";
import { tUi } from "../../utils/dq_lc";
import "../../components/TutorialSystem/TutorialSystem.css";
import { ONBOARDING_STEPS_BY_PLAN, CLIENT_ONBOARDING_STEPS_BY_PLAN, TEACHER_ONBOARDING_STEPS_BY_PLAN, pbdqClientHomeSteps, withPipelineWaitingBadge, hasPipelineWaitingBadge } from "./DQProV2/constants/onboardingSteps.config";
import ConfirmationDialog from "./components/ConfirmationDialog.comp";
import FloatingNudge from "../../components/FloatingNudge/FloatingNudge.comp";
import UnlockCapabilityDialog from './DQProV2/components/UnlockCapabilityDialog.comp';

// Path of the owner's "View Requests" CTA — used to attach the slide-181
// "N Requests" badge to that specific button.
const PROGRAM_REQUESTS_PATH = '/dq_pro/program/requests';

// Path of the school's Step 4 CTA — carries the slide-305 delivery notification.
const SCHOOL_PROGRAM_REQUEST_PATH = '/dq_pro/program/request';

// The Parental Consent set-up (SNW, SW Parental Consent Flow, 5 Oct 2026).
const PARENTAL_CONSENT_PATH = '/dq_pro/parental-consent';

/**
 * Step 1's parental-consent block on the School / School Network board
 * (slides 4, 6, 20, 21, 37): a status line and a Set Up / View button, above
 * the step's own CTA. Until consent is set up that CTA (Add Schools / Add
 * Classes) is disabled — "Set up parental consent first."
 */
const ConsentStepBlock = ({ consentSet, history }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
            {consentSet
                ? <CheckCircleOutlineIcon style={{ fontSize: 17, color: '#1E7B3A' }} />
                : <InfoOutlinedIcon style={{ fontSize: 17, color: '#8A8F98' }} />}
            <Typography sx={{ fontSize: '13.5px', color: consentSet ? '#1E7B3A' : '#8A8F98' }}>
                {consentSet ? 'Parental consent is set up.' : 'Start by setting up parental consent.'}
            </Typography>
        </Box>
        <Button
            onClick={() => history.push(PARENTAL_CONSENT_PATH)}
            sx={{
                alignSelf: 'stretch',
                backgroundColor: consentSet ? 'var(--brand-surface, #fff)' : '#FF6900',
                color: consentSet ? '#E8650C' : '#ffffff',
                border: consentSet ? '1px solid #FF6900' : '0',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: 1.3,
                px: 1.25,
                py: 1,
                borderRadius: '7px',
                '&:hover': { backgroundColor: consentSet ? '#FFF4EA' : '#e55a00' },
            }}
        >
            {consentSet ? 'View Parental Consent' : 'Set Up Parental Consent'}
        </Button>
    </Box>
);

const PlanStepCard = ({ step, history, onCtaAction, isCtaActivated, requestCount, notifyCount, onNotifySeen, onUnlock, centered = false }) => {
    // Sequentially-locked steps (see ONBOARDING_STEPS_BY_PLAN) carry an explicit
    // `locked` flag computed from progress; steps without one (e.g. P1's bookend
    // upgrade cards) fall back to the older isUpgrade-only lock.
    const locked = step.locked !== undefined ? step.locked : (step.isUpgrade && !step.clickable);
    // Dark mode: a locked card was the light grey below — a white card in a dark
    // board, its pale text barely readable. It takes a faint light tint instead,
    // and its disabled button and Unlock pill a dark-board shade likewise.
    const { isDark } = useColorMode();
    const disabledBtnBg = isDark ? 'rgba(255,255,255,0.12)' : '#CBD1D8';
    const disabledBtnText = isDark ? 'rgba(255,255,255,0.45)' : '#ffffff';
    return (
    <Box sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${locked ? (isDark ? 'rgba(255,255,255,0.08)' : '#E6EAF0') : "var(--brand-border,'#E5E7EB')"}`,
        borderRadius: '12px',
        // NOT clipped: the deck draws the "Unlock" pill straddling the card's top
        // border, half in and half out. Clipping would slice it in two.
        // Locked cards take a flat, barely-tinted grey instead of a veil. Kept
        // light on purpose: a stronger blue-grey made the whole right-hand side
        // of the board read as navy.
        backgroundColor: locked ? (isDark ? 'rgba(255,255,255,0.04)' : '#F1F4F8') : 'var(--brand-surface, #ffffff)',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
        opacity: 1,
        pointerEvents: locked ? 'none' : 'auto',
        cursor: locked ? 'not-allowed' : 'default',
    }}>
        {/* Slide 305 — delivery notification on the corner of Step 4. zIndex 3
            keeps it above the card content so a school still sees that something
            arrived even while the step is locked. */}
        {notifyCount > 0 && (
            <Tooltip
                arrow
                placement="left"
                title={`${notifyCount} new learning ${notifyCount === 1 ? 'program' : 'programs'} delivered by your DQ Partner. Open this step to view ${notifyCount === 1 ? 'it' : 'them'}.`}
                componentsProps={{
                    // Brand orange, matching the badge it describes.
                    tooltip: { sx: { backgroundColor: '#FF6900', color: '#fff', fontSize: 12.5, fontWeight: 600, px: 1.5, py: 1 } },
                    arrow: { sx: { color: '#FF6900' } },
                }}
            >
                <Box sx={{
                    position: 'absolute', top: 10, right: 10, zIndex: 3,
                    minWidth: 24, height: 24, borderRadius: '12px', px: 0.75,
                    backgroundColor: '#FF6900', color: '#ffffff',
                    fontSize: '13px', fontWeight: 700, lineHeight: 1,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.18)', cursor: 'help',
                }}>
                    {notifyCount}
                </Box>
            </Tooltip>
        )}
        {/*
          * No blur veil.
          *
          * There used to be a blurred overlay here. The deck does not blur locked
          * cards at all — it tints them very lightly and mutes the text,
          * which stays readable. Blurring made Step 4's description unreadable, so
          * a partner could not tell what they would be unlocking.
          *
          * The locked look now comes from the card's own background and text
          * colours below, which is also one less stacking layer to fight with.
          */}
        {/*
          * The "Unlock" pill.
          *
          * The PW deck draws a dark rounded pill sitting on the top edge of every
          * locked card — not the full-width "Upgrade" bar this used to render.
          * The word is "Unlock" on all three package variants (slides 3-5).
          *
          * Step 5's pill is deliberately dead: the slide is annotated "Disable
          * Step 5 unlock", and the Button Registry records its destination as
          * "Slide code needed". `unlockDisabled` on the step carries that.
          */}
        <Box sx={{
            // Straddles the top border, as drawn.
            mt: (step.isUpgrade || step.topBadge) ? '-15px' : 0,
            height: (step.isUpgrade || step.topBadge) ? 'auto' : '18px',
            display: 'flex',
            justifyContent: 'center',
            visibility: (step.isUpgrade || step.topBadge) ? 'visible' : 'hidden',
            position: 'relative',
            zIndex: 3,
            // The card sets pointerEvents:none while locked so its CTA cannot be
            // clicked through the veil. The Unlock pill is the one control that
            // MUST stay live on a locked card, so it opts back in.
            pointerEvents: 'auto',
        }}>
            {/*
              * The slide's red count pill — "3 waiting" on Step 3, pointing at
              * PW-ALL-Growth-Pipeline's "# of Open Opportunities".
              *
              * PLACEHOLDER TEXT. The real number spans unlock, credit, licence and
              * program requests; only program requests exist, and that list is
              * enterprise_p1-only, so there is nothing to count yet. `topBadge`
              * becomes a number once the pipeline API lands.
              */}
            {!step.isUpgrade && step.topBadge && (
                <Box sx={{
                    borderRadius: '999px',
                    px: 2,
                    py: '4px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    color: '#ffffff',
                    backgroundColor: step.topBadgeColor || '#D92D20',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                }}>
                    {step.topBadge}
                </Box>
            )}
            {step.isUpgrade && (
                <Box
                    component="button"
                    type="button"
                    disabled={!!step.unlockDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (step.unlockDisabled || !onUnlock) return;
                        onUnlock(step);
                    }}
                    sx={{
                        border: 'none',
                        borderRadius: '999px',
                        px: 2.5,
                        py: '5px',
                        fontSize: '13px',
                        fontWeight: 700,
                        letterSpacing: '0.4px',
                        color: '#ffffff',
                        backgroundColor: step.unlockDisabled ? '#9CA3AF' : (isDark ? '#4B5563' : '#1F2937'),
                        cursor: step.unlockDisabled ? 'not-allowed' : 'pointer',
                        opacity: step.unlockDisabled ? 0.7 : 1,
                        '&:hover': { backgroundColor: step.unlockDisabled ? '#9CA3AF' : (isDark ? '#6B7280' : '#111827') },
                    }}
                >
                    Unlock
                </Box>
            )}
        </Box>
        {/*
          * The PbDQ deck centres every card: step number, title, description and
          * button. Other step sets (level-22 clients, the P1 Workforce journey)
          * keep the left alignment they were designed with, hence the prop.
          */}
        <Box sx={{ p: centered ? 2.25 : 3, display: 'flex', flexDirection: 'column', flexGrow: 1, textAlign: centered ? 'center' : 'left' }}>
            <Typography sx={{ color: locked ? '#F5C7A8' : '#FF6900', fontSize: '23px', fontWeight: 'bold', letterSpacing: '0.4px', mb: 0.5 }}>
                {step.stepNum}
            </Typography>
            <Typography sx={{ color: locked ? '#9BA1A9' : 'var(--brand-text, #111827)', fontSize: '19px', fontWeight: 700, mb: 1 }}>
                {step.title}
            </Typography>
            <Typography sx={{ color: locked ? '#A8ADB5' : 'var(--brand-text-muted, #374151)', fontSize: '15px', lineHeight: 1.6, flexGrow: 1, mb: 2.5 }}>
                {step.desc}
            </Typography>
            {step.ctas ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {step.ctas.map(c => {
                        // Already-activated action CTAs render as a disabled "Activated" state.
                        const activated = isCtaActivated ? isCtaActivated(c) : false;
                        // All CTAs on a card activate together once the card itself unlocks.
                        const ctaClickable = !locked && !activated;
                        // Slide 181 — show the pending-requests count on the
                        // "View Requests" CTA when we have a number for it.
                        const showBadge = c.path === PROGRAM_REQUESTS_PATH
                            && typeof requestCount === 'number' && requestCount > 0;
                        return (
                        <Box key={c.label} sx={{ position: 'relative' }}>
                            <Button
                                onClick={() => {
                                    if (!ctaClickable) return;
                                    // Opening Step 4 IS the acknowledgement (slide 305):
                                    // clear the badge before navigating away.
                                    if (c.path === SCHOOL_PROGRAM_REQUEST_PATH && onNotifySeen) onNotifySeen();
                                    if (c.action && onCtaAction) onCtaAction(c);
                                    else if (c.path) history.push(c.path);
                                }}
                                sx={{
                                    width: '100%',
                                    /*
                                     * Orange when live, grey when not — the same
                                     * two states the single-CTA cards use, and what
                                     * the deck draws: Steps 1-3 orange, the locked
                                     * Steps 4-5 grey. This used to be black, which
                                     * put two different button colours on one board.
                                     *
                                     * Only the PbDQ Partner Home uses multi-CTA
                                     * cards, so nothing else is affected.
                                     */
                                    backgroundColor: ctaClickable ? '#FF6900' : `${disabledBtnBg} !important`,
                                    color: ctaClickable ? '#ffffff' : `${disabledBtnText} !important`,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    lineHeight: 1.3,
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: '7px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    cursor: ctaClickable ? 'pointer' : 'not-allowed !important',
                                    '&:hover': {
                                        backgroundColor: ctaClickable ? '#e55a00' : `${disabledBtnBg} !important`,
                                        cursor: ctaClickable ? 'pointer' : 'not-allowed !important',
                                    },
                                }}
                            >
                                {activated && (
                                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3DDC84', flexShrink: 0 }}/>
                                )}
                                {c.label}
                                {showBadge && (
                                    <Box component="span" sx={{
                                        backgroundColor: '#FF6900', color: '#ffffff',
                                        fontSize: '12px', fontWeight: 700, lineHeight: 1,
                                        minWidth: 20, height: 20, borderRadius: '10px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        px: 0.75,
                                    }}>
                                        {requestCount}
                                    </Box>
                                )}
                            </Button>
                        </Box>
                        );
                    })}
                </Box>
            ) : step.cta ? (
                <>
                {step.consent && <ConsentStepBlock consentSet={step.consent.set} history={history} />}
                <Tooltip title={step.consent && !step.consent.set ? 'Set up parental consent first.' : ''} arrow>
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', cursor: step.consent && !step.consent.set ? 'not-allowed' : 'default' }}>
                <Button
                    disabled={locked || (step.consent && !step.consent.set)}
                    onClick={() => {
                        if (locked || !step.path) return;
                        // Opening Step 4 IS the acknowledgement (slide 305):
                        // clear the badge before navigating away.
                        if (step.path === SCHOOL_PROGRAM_REQUEST_PATH && onNotifySeen) onNotifySeen();
                        history.push(step.path);
                    }}
                    sx={{
                        /*
                         * Full width on the PbDQ board so all five buttons line up
                         * across the row, as the deck draws them. Left-aligned and
                         * content-sized everywhere else, which is how the other
                         * card sets were designed.
                         */
                        alignSelf: centered ? 'stretch' : 'flex-start',
                        backgroundColor: (locked || (step.consent && !step.consent.set)) ? `${disabledBtnBg} !important` : '#FF6900',
                        color: (locked || (step.consent && !step.consent.set)) ? `${disabledBtnText} !important` : '#ffffff !important',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: 1.3,
                        px: 1.25,
                        py: 1,
                        borderRadius: '7px',
                        '&:hover': { backgroundColor: locked ? undefined : '#e55a00' },
                    }}
                >
                    {step.cta}
                </Button>
                </Box>
                </Tooltip>
                </>
            ) : null}
        </Box>
    </Box>
    );
};

const WorkspaceHome = ({history, onCreateProgram, onCreateAssessment, onCreateCpciAssessment, isLevel22, authUser}) => {
    const { setAuthUser } = useAuth();
    const isTeacherUser = isTeacher(authUser);
    const planKey = authUser?.dq_pro_plan?.plan_key;
    /**
     * The PbDQ Partner Home — the five-card layout from deck slides 3, 4 and 5.
     *
     * All four PbDQ partner plans draw the same board, so the layout flag covers
     * them all rather than enterprise_p0 alone. enterprise_p1 is NOT one of them:
     * that plan key carries the separate Workforce journey (Define / Setup /
     * Baseline / Build / Track / Certify), which keeps its own three-up layout.
     */
    const isPbdqPartnerHome = [
        DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
        DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
        DQ_PRO_PLAN_KEY.ENTERPRISE_P1A,
        DQ_PRO_PLAN_KEY.ENTERPRISE_P1B,
        DQ_PRO_PLAN_KEY.ENTERPRISE_LC,
    ].includes(planKey);
    const isP1 = planKey === 'enterprise_p1';
    // Teacher steps reuse the exact same 5-card board style/layout as the PbDQ
    // Partner Home (centered cards, one row of five) — but a teacher's
    // dq_pro_plan.plan_key isn't guaranteed to match one of the plan keys
    // above (it may be missing/inherited differently), so the layout can't
    // depend on that match the way the Partner Home itself does. isTeacherUser
    // is the reliable signal here; TEACHER_ONBOARDING_STEPS_BY_PLAN already
    // has its own safe fallback for an unrecognized plan_key.
    const showsPartnerBoardStyle = isPbdqPartnerHome || isTeacherUser;

    const [showActivateWarning, setShowActivateWarning] = useState(false);

    // Try button just opens the "you'll be logged out" warning; actual activation
    // only runs once the user confirms.
    const handleActivateProduct = () => {
        setShowActivateWarning(true);
    };

    // Self-activate a product the insider's package marks "available" (e.g. learning_program from LearnCreator).
    const confirmActivateProduct = async () => {
        setShowActivateWarning(false);
        const available = authUser?.available_products || [];
        const product_key = available.includes(PRODUCT_KEY.LEARNING_PROGRAM)
            ? PRODUCT_KEY.LEARNING_PROGRAM
            : available[0];
        if (!product_key) {
            toast(TOAST_TYPES.WARN, 'Unavailable', 'No product is available to activate.');
            return;
        }
        try {
            await activateProductService(product_key);
            // Activate invalidates the session — re-fetch so product_access reflects the change.
            const res = await getUserDetailsService();
            const refreshedUser = res?.data?.user || res?.data;
            if (refreshedUser) setAuthUser(refreshedUser);
            toast(TOAST_TYPES.SUCCESS, 'Activated', 'LearnCreator is now enabled.');
            history.push('/dq_pro/create');
        } catch (e) {
            toast(TOAST_TYPES.DANGER, 'Activation failed', 'Could not activate the product. Please try again.');
        }
    };

    const defaultSteps = [
        {label: 'STEP 1', title: 'Create', desc: 'Build your own learning program with a built-in assessment.'},
        {label: 'STEP 2', title: 'Track', desc: 'Check learner progress via built-in assessment results.'},
        {label: 'STEP 3', title: 'Improve', desc: 'Refine your program and redeploy with confidence.'},
    ];

    const actions = [
        {
            title: 'Create a Program',
            desc: 'Design and deliver an AI literacy learning experience — assessment included.',
            cta: 'Create Program',
            color: 'var(--brand-global-1, #FF6900)',
            onClick: onCreateProgram,
            hidden: isLevel22 || isTeacherUser || !isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM),
        },
        {
            title: 'Run an Assessment',
            desc: 'Want to measure something specific — online safety, AI-readiness? Run a focused standard assessment now.',
            cta: 'Start Assessment',
            color: 'var(--brand-global-2, #7B8FC7)',
            onClick: onCreateAssessment,
            hidden: isLevel22 || isTeacherUser || !isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT),
        },
        {
            title: 'Run a CPC Index Assessment',
            desc: 'Measure how ready and protected young people are across the CPC Index competencies.',
            cta: 'Start CPC Index Assessment',
            color: 'var(--brand-global-3, #0097B2)',
            onClick: onCreateCpciAssessment,
            hidden: isLevel22 || isTeacherUser || !isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT),
        },
        {
            title: 'View Your Dashboard',
            desc: 'Monitor learner progress, outcomes, and program impact across all your groups.',
            cta: 'View Impact',
            color: '#B91C6C',
            onClick: () => history.push('/dq_pro/partner-dashboard/overview'),
            // Owner's Partner Dashboard is a PLAN FEATURE (partner_dashboard), not a
            // product — gate on the feature so it shows whenever the plan grants it.
            hidden: isLevel22 || isTeacherUser || !isFeatureEnabled(authUser, FEATURE_KEY.PARTNER_DASHBOARD),
        },
    ].filter(a => !a.hidden);

    const completedSteps = new Set(authUser?.onboarding_steps_completed || []);

    /*
     * Step 4's chip on the PbDQ School / School Network board — how many program
     * suggestions DQ Pro has sent that still wait for an answer: a network's
     * across its schools, a school's its own (bug sheet Ian-SNW-29Sep-5). It
     * used to be a fixed "1 new" on every account. Rides on the existing
     * requests list, which scopes the count to the caller.
     */
    const isPbdqClientBoard = !isTeacherUser && isLevel22
        && !!pbdqClientHomeSteps(planKey, authUser?.is_school_network);
    const [suggestionsPending, setSuggestionsPending] = useState(0);
    // A school with LearnCreator (LC or P1B) builds its own programs: no
    // suggestions chip (LA-PW,SNW,SW-21Oct-01).
    const buildsOwnPrograms = canCreateProduct(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    useEffect(() => {
        if (!isPbdqClientBoard || buildsOwnPrograms) return undefined;
        let alive = true;
        listProgramRequestsService({ page: 1, limit: 1 })
            .then((res) => { if (alive) setSuggestionsPending(Number(res?.data?.suggestions_pending_total) || 0); })
            .catch(() => { /* best-effort — no chip on failure */ });
        return () => { alive = false; };
    }, [isPbdqClientBoard, buildsOwnPrograms]);

    /*
     * Parental consent on Step 1 of the School / School Network board (SNW, SW
     * Parental Consent Flow, 5 Oct 2026). null while unknown — the Add button is
     * only held back once the server has said consent is NOT set up; the class
     * publish itself is refused server-side either way.
     */
    const [consentSet, setConsentSet] = useState(null);
    useEffect(() => {
        if (!isPbdqClientBoard) return undefined;
        let alive = true;
        getConsentSetupService()
            .then((res) => { if (alive) setConsentSet(!!res?.data?.consent_pathway); })
            .catch(() => { /* best-effort — the board renders without the gate */ });
        return () => { alive = false; };
    }, [isPbdqClientBoard]);

    /*
     * Step 3's chip on the PbDQ Partner board — requests from networks, schools
     * and teachers in Open and In Progress: aligned with the Pipeline's Open and
     * In Progress tab counts. Not fetched where Step 3 is locked (LC) or the plan
     * has no Partner board.
     */
    const isPbdqPartnerBoard = !isTeacherUser && !isLevel22 && hasPipelineWaitingBadge(ONBOARDING_STEPS_BY_PLAN[planKey]);
    const [pipelineWaiting, setPipelineWaiting] = useState(0);
    useEffect(() => {
        if (!isPbdqPartnerBoard) return undefined;
        let alive = true;
        listProgramRequestsService({ page: 1, limit: 1 })
            .then((res) => {
                if (alive) {
                    const tabCounts = res?.data?.tab_counts;
                    if (tabCounts) {
                        const openCount = Number(tabCounts.open) || 0;
                        const inProgressCount = Number(tabCounts.in_progress) || 0;
                        setPipelineWaiting(openCount + inProgressCount);
                    } else {
                        // Fallback to total if tab_counts is not provided
                        setPipelineWaiting(Number(res?.data?.total) || 0);
                    }
                }
            })
            .catch(() => { /* best-effort — no chip on failure */ });
        return () => { alive = false; };
    }, [isPbdqPartnerBoard]);

    // Level-22 clients (educators created under an owner) get the school-scoped
    // step set; the owner gets the network-scoped set. Client steps aren't
    // sequentially locked.
    const stepsForPlanRaw = isTeacherUser
        // A teacher's plan is inherited from their school. Fall back to the
        // LC set (the more locked-down of the two) for a plan_key this map
        // doesn't recognize, rather than risk showing steps the school isn't
        // entitled to.
        ? (TEACHER_ONBOARDING_STEPS_BY_PLAN[planKey] || TEACHER_ONBOARDING_STEPS_BY_PLAN[DQ_PRO_PLAN_KEY.ENTERPRISE_LC])
        : isLevel22
            /*
             * PbDQ plans get the five-card School / School Network board from the
             * workspace decks; the network variant differs only in wording. Any
             * other plan falls through to the older client board, so non-PbDQ
             * customers keep exactly the Home they have today.
             */
            ? (pbdqClientHomeSteps(planKey, authUser?.is_school_network, suggestionsPending)
                // A direct plan reads as its solution (direct_p0 → enterprise_p0).
                || CLIENT_ONBOARDING_STEPS_BY_PLAN[solutionPlanKey(planKey)]
                || null)
            : (ONBOARDING_STEPS_BY_PLAN[planKey]
                ? withPipelineWaitingBadge(ONBOARDING_STEPS_BY_PLAN[planKey], pipelineWaiting)
                : null);
    // Owner-only cards (Step 4 / LearnCreator upsell) are hidden from peers —
    // colleague logins that share the owner's workspace and plan. Step 4 is the
    // last card, so filtering it never disturbs the 1→2→3 sequential locking.
    // requiresCapability steps (teacher set) are HIDDEN, not shown disabled,
    // for a teacher without the LearnCreator capability — a two-step home is
    // the correct state for them, not a degraded one.
    const stepsForPlan = stepsForPlanRaw
        ? stepsForPlanRaw
            .filter((s) => !(s.ownerOnly && authUser?.is_peer === true))
            .filter((s) => !(s.requiresCapability && !isLcTeacher(authUser)))
        : null;
    const planSteps = stepsForPlan
        ? stepsForPlan.map((step, idx) => step.key
            ? { ...step, locked: idx === 0 ? false : !completedSteps.has(stepsForPlan[idx - 1].key) }
            : step)
            // Step 1 carries the parental-consent block on the School / Network board.
            .map((step, idx) => (isPbdqClientBoard && idx === 0 && consentSet !== null
                ? { ...step, consent: { set: consentSet } }
                : step))
        : null;

    // Slide 181 — badge the "View Requests" CTA with how many program requests
    // are waiting. Only fetch when a card actually carries that CTA (owner with
    // the requests card), so no other plan/user makes this call.
    const hasRequestsCard = !!(stepsForPlan || []).some(
        (s) => Array.isArray(s.ctas) && s.ctas.some((c) => c.path === '/dq_pro/program/requests'),
    );
    // Slide 305 — the school's Step 4 carries a notification for programs their
    // DQ Partner delivered but they have not opened yet. Only fetched when a
    // card actually links to the school request page, so owners never call it.
    const hasSchoolDeliverCard = !!(stepsForPlan || []).some(
        (s) => s.path === SCHOOL_PROGRAM_REQUEST_PATH,
    );
    // The count rides along on the existing requests list (server scopes it to
    // the caller's own org), so the badge needs no endpoint of its own.
    const [deliveryNotifyCount, setDeliveryNotifyCount] = useState(0);

    // The Unlock dialog behind every locked step card. One dialog for the whole
    // page rather than one per card — it lists every capability regardless of
    // which card was clicked, exactly as the deck draws it.
    const [unlockOpen, setUnlockOpen] = useState(false);
    useEffect(() => {
        if (!hasSchoolDeliverCard) return undefined;
        let alive = true;
        listProgramRequestsService({ page: 1, limit: 1 })
            .then((res) => { if (alive) setDeliveryNotifyCount(Number(res?.data?.unseen_delivered_total) || 0); })
            .catch(() => { /* best-effort — no badge on failure */ });
        return () => { alive = false; };
    }, [hasSchoolDeliverCard]);

    /**
     * The school clicked into Step 4 — acknowledge every delivered program at
     * once so a batch clears together. Optimistic: the badge hides immediately
     * and the write is fire-and-forget, because a failed acknowledgement must
     * never block navigation (the count simply reappears on the next load).
     */
    const handleDeliveriesSeen = useCallback(() => {
        setDeliveryNotifyCount(0);
        markSchoolDeliveriesSeenService().catch(() => {});
    }, []);

    const [requestCount, setRequestCount] = useState(null);
    useEffect(() => {
        if (!hasRequestsCard) return;
        let alive = true;
        listProgramRequestsService({ page: 1, limit: 1 })
            .then((res) => { if (alive) setRequestCount(Number(res?.data?.total) || 0); })
            .catch(() => { /* best-effort — no badge on failure */ });
        return () => { alive = false; };
    }, [hasRequestsCard]);

    return (
        <Box sx={{
            px: {xs: 2, sm: 0, md: 0},
            py: {xs: 3, sm: 5},
            maxWidth: '1200px',
            mx: 'auto',
            // Vertically center the whole board within the page — every plan's
            // workspace home (owner, client, teacher) gets this, not just
            // level-22 clients as before.
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            <Typography sx={{
                textAlign: 'center',
                color: '#FF6900',
                fontSize: {xs: '28px', sm: '36px'},
                fontWeight: 600,
                mb: 1,
            }}>
                Welcome to your workspace
            </Typography>

            <Typography sx={{
                textAlign: 'center',
                color: 'var(--brand-text, #111827)',
                fontSize: showsPartnerBoardStyle ? {xs: '15px', sm: '17px'} : {xs: '22px', sm: '28px'},
                fontWeight: showsPartnerBoardStyle ? 400 : 700,
                mt: showsPartnerBoardStyle ? 1 : 3,
                mb: 1,
            }}>
                {/*
                  * The deck's own strapline on the PbDQ Home, verbatim from
                  * slides 3-5, with a school-scoped variant for the teacher
                  * board (same design family, teacher's own copy). Other
                  * plans keep the generic greeting.
                  */}
                {(isTeacherUser || isLevel22)
                    /*
                     * School, School Network and Teacher all share this line.
                     * Verbatim from School deck slides 3-5 and Network deck
                     * slides 2-4 — the Network wording is "your school" too,
                     * not "your schools", which is why there is no variant.
                     */
                    ? "Everything your school needs to assess, deliver learning, and monitor impact."
                    : isPbdqPartnerHome
                        ? 'Everything you need to assess schools, deliver learning, and demonstrate impact.'
                        : "Good to have you here. Let's get started."}
            </Typography>

            {!showsPartnerBoardStyle && (
                <Typography sx={{
                    textAlign: 'center',
                    color: 'var(--brand-text-muted, #374151)',
                    fontSize: {xs: '15px', sm: '16px'},
                    mb: 4,
                }}>
                    This is where you design learning, run assessments, and see the impact you're making.
                </Typography>
            )}

            {showsPartnerBoardStyle && <Box sx={{ mb: 4 }} />}

            {/* Plan-specific step views — owner (network), client (school) and
                teacher all show cards. Teacher steps reuse the exact same PbDQ
                Partner Home card style (5-across, centered) — see
                showsPartnerBoardStyle above for why that isn't driven by
                isPbdqPartnerHome's plan_key match alone. */}
            {planSteps && (
                <Box sx={{
                    display: 'grid',
                    /*
                     * The deck lays the PbDQ Home out as ONE row of five equal
                     * cards. Column count follows the card count so the fifth
                     * card does not wrap onto a row of its own.
                     *
                     * Educators/peers see 3 cards, and the P1 Workforce journey
                     * keeps its 3-column layout for 6 cards.
                     */
                    gridTemplateColumns: (showsPartnerBoardStyle && planSteps.length >= 4)
                        ? { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${planSteps.length}, 1fr)` }
                        : { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 3,
                }}>
                    {planSteps.map(step => (
                        <PlanStepCard
                            key={step.stepNum}
                            step={step}
                            history={history}
                            onCtaAction={handleActivateProduct}
                            isCtaActivated={(c) => c.action === 'activate_product' && isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM)}
                            requestCount={requestCount}
                            notifyCount={step.path === SCHOOL_PROGRAM_REQUEST_PATH ? deliveryNotifyCount : 0}
                            onNotifySeen={handleDeliveriesSeen}
                            onUnlock={() => setUnlockOpen(true)}
                            centered={showsPartnerBoardStyle}
                        />
                    ))}
                </Box>
            )}

            {/* Default 3-step view for all other plans */}
            {!isLevel22 && !planSteps && (
                <>
                    <Box sx={{
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '14px',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
                        mb: 5,
                        overflow: 'hidden',
                        maxWidth: '650px',
                        mx: 'auto',
                        width: '100%',
                    }}>
                        {defaultSteps.map((s, i) => (
                            <Box key={s.label} sx={{
                                p: {xs: 3, sm: 4},
                                borderRight: {xs: 'none', sm: i < defaultSteps.length - 1 ? '1px solid var(--brand-border, #E5E7EB)' : 'none'},
                                borderBottom: {xs: i < defaultSteps.length - 1 ? '1px solid var(--brand-border, #E5E7EB)' : 'none', sm: 'none'},
                            }}>
                                <Typography sx={{color: '#FF6900', fontSize: '14px', fontWeight: 700, letterSpacing: '0.5px', mb: 1.5}}>
                                    {s.label}
                                </Typography>
                                <Typography sx={{color: 'var(--brand-text, #111827)', fontSize: '18px', fontWeight: 700, mb: 1}}>
                                    {s.title}
                                </Typography>
                                <Typography sx={{color: 'var(--brand-text-muted, #374151)', fontSize: '15px', lineHeight: 1.5}}>
                                    {s.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

            {actions.length > 0 && <Box sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: `repeat(${Math.min(actions.length, 3)}, 1fr)`},
                gap: 3,
            }}>
                {actions.map((a) => (
                    <Box key={a.title} sx={{
                        border: '1px solid var(--brand-border, #E5E7EB)',
                        borderRadius: '14px',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <Typography sx={{color: 'var(--brand-text, #0A0A0A)', fontSize: '18px', fontWeight: 700, mb: 1.5}}>
                            {a.title}
                        </Typography>
                        <Typography sx={{color: 'var(--brand-text-muted, #6A7282)', fontSize: '14px', lineHeight: 1.5, mb: 3, flexGrow: 1}}>
                            {a.desc}
                        </Typography>
                        <Button
                            onClick={a.onClick}
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: a.color,
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                                py: 1,
                                borderRadius: '8px',
                                '&:hover': {backgroundColor: a.color, opacity: 0.92},
                            }}
                        >
                            {a.cta}
                        </Button>
                    </Box>
                ))}
            </Box>}
                </>
            )}

            <UnlockCapabilityDialog

                open={unlockOpen}

                onClose={() => setUnlockOpen(false)}

                authUser={authUser}

            />

            <ConfirmationDialog
                open={showActivateWarning}
                onClose={() => setShowActivateWarning(false)}
                onConfirm={confirmActivateProduct}
                title="You Will Be Logged Out"
                message="Activating LearnCreator will end your current session and log you out. You'll need to log back in afterwards. Do you want to proceed?"
                confirmText="Proceed"
                cancelText="Cancel"
                isWarning={true}
            />
        </Box>
    );
};

// ─── Classes Independent Nudge (Tutorial A) ──────────────────────────────────
// A standalone floating tooltip next to the "Classes" menu item.
// Does NOT use the tutorial overlay system.

const ClassesIndependentNudge = ({ onDismiss }) => {
    const locale = localStorage.getItem('dq_locale') || 'en-US';
    // Behaviour unchanged — now backed by the reusable FloatingNudge (adds an X button).
    return (
        <FloatingNudge anchorSelector="#sidebar-classes-link" onDismiss={onDismiss}>
            {tUi('tutorial.classesNudge.content', locale)}
        </FloatingNudge>
    );
};

// ─── Chat Nudge Banner (Tutorial B) ───────────────────────────────────────────
// Inline dismissable banner shown when the user has an active chat but the
// program build hasn't started yet. Does NOT use the tutorial overlay system.

const ChatNudgeBanner = ({ authUser, history, onDismiss }) => {
    const locale = localStorage.getItem('dq_locale') || 'en-US';
    return (
        <Box
            className="tutorial-chat-banner"
            sx={{
                mx: { xs: 2, sm: 4, md: 8 },
                mt: 4,
                mb: 2,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #052F44 0%, #0a4a6e 100%)',
                boxShadow: '0 8px 32px rgba(5,47,68,0.25)',
                p: { xs: '18px 20px', sm: '20px 28px' },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, sm: 3 },
                flexWrap: 'wrap',
                position: 'relative',
                overflow: 'hidden',
                maxWidth: '1200px',
            }}
        >
            {/* Decorative accent stripe */}
            <Box sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '5px',
                background: 'linear-gradient(180deg, #FF6900 0%, #e55a00 100%)',
                borderRadius: '14px 0 0 14px',
            }} />

            {/* Icon */}
            <Box sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(255,105,0,0.18)',
                border: '1px solid rgba(255,105,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 22,
                ml: '12px',
            }}>
                🤖
            </Box>

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    color: '#FF6900',
                    fontSize: { xs: '14px', sm: '15px' },
                    fontWeight: 700,
                    lineHeight: 1.3,
                    mb: 0.5,
                }}>
                    {tUi('tutorial.chatNudge.banner.title', locale)}
                </Typography>
                <Typography sx={{
                    color: 'rgba(255,255,255,0.72)',
                    fontSize: { xs: '12px', sm: '13px' },
                    lineHeight: 1.5,
                }}>
                    {tUi('tutorial.chatNudge.banner.content', locale)}
                </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Button
                    onClick={() => history.push('/dq_pro/program/creation/preview')}
                    sx={{
                        background: 'linear-gradient(135deg, #FF6900 0%, #e55a00 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: { xs: '12px', sm: '13px' },
                        px: { xs: 2, sm: 2.5 },
                        py: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        boxShadow: '0 2px 12px rgba(255,105,0,0.4)',
                        '&:hover': {
                            background: '#e55a00',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(255,105,0,0.5)',
                        },
                        transition: 'all 0.18s ease',
                    }}
                >
                    {tUi('tutorial.chatNudge.banner.btn', locale)}
                </Button>
                {/*<Button*/}
                {/*    onClick={onDismiss}*/}
                {/*    sx={{*/}
                {/*        color: 'rgba(255,255,255,0.55)',*/}
                {/*        fontSize: { xs: '11px', sm: '12px' },*/}
                {/*        fontWeight: 500,*/}
                {/*        textTransform: 'none',*/}
                {/*        minWidth: 0,*/}
                {/*        px: 1,*/}
                {/*        '&:hover': { color: 'rgba(255,255,255,0.85)', background: 'transparent' },*/}
                {/*    }}*/}
                {/*>*/}
                {/*    {tUi('tutorial.chatNudge.banner.dismiss', locale)}*/}
                {/*</Button>*/}
            </Box>
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const DQProHome = () => {
    const classes = useStyles();
    const history = useHistory();
    const { authUser } = useAuth();
    const isLevel21 = authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    /*
     * "Is this a client workspace?" — a school (22) or a school network (21).
     * The name is historical; a network reaches this board too, and comparing
     * to 22 alone would hand it the PARTNER Home instead of its own.
     */
    const isLevel22 = isClientWorkspace(authUser);

    // Dialog state (same flow as DQProProducts)
    const [open, setOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [hasExistingProgram, setHasExistingProgram] = useState(false);
    const [showExistingConfirmation, setShowExistingConfirmation] = useState(false);
    const [showClassesNudge, setShowClassesNudge] = useState(false);
    const [showChatBanner, setShowChatBanner] = useState(false);
    const isStartingNewRef = React.useRef(false);

    // ── Contextual / situational tutorial hooks ────────────────────────────────
    const { shouldShowClassesNudge, shouldShowChatBanner } = useSituationalTutorials(authUser);

    // Tutorial A — show the independent classes tooltip
    useEffect(() => {
        if (shouldShowClassesNudge) setShowClassesNudge(true);
    }, [shouldShowClassesNudge]);

    const handleDismissClassesNudge = () => {
        setShowClassesNudge(false);
        if (authUser?.id) {
            localStorage.setItem(`tutorial_classes_nudge_${authUser.id}`, '1');
        }
    };

    // Tutorial B — show the inline chat banner
    useEffect(() => {
        if (shouldShowChatBanner) setShowChatBanner(true);
    }, [shouldShowChatBanner]);

    const handleDismissChatBanner = () => {
        setShowChatBanner(false);
        if (authUser?.id) {
            localStorage.setItem(`tutorial_chat_nudge_${authUser.id}`, '1');
        }
    };

    // ── Existing program detection for Create Program dialog ─────────────────
    useEffect(() => {
        let isMounted = true;
        getActiveChatInsiderProgramService()
            .then(res => { if (isMounted) setHasExistingProgram(!!res?.data?.program_id); })
            .catch(() => {});
        return () => { isMounted = false; };
    }, []);

    const handleStartNew = async () => {
        if (isStartingNewRef.current) return;
        isStartingNewRef.current = true;
        try {
            const res = await getActiveChatInsiderProgramService();
            const programId = res?.data?.program_id;
            if (programId) {
                try { await clearChatStateInsiderService(programId); } catch (e) {}
            }
        } catch (e) {}
        finally {
            setOpen(false);
            setDialogType(null);
            history.push('/dq_pro/program/creation/preview');
            isStartingNewRef.current = false;
        }
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setDialogType(null);
        setShowExistingConfirmation(false);
    };

    return (
        <>
            <WorkspaceHome
                history={history}
                isLevel22={isLevel22}
                authUser={authUser}
                onCreateProgram={() => { setDialogType('program'); setOpen(true); }}
                onCreateAssessment={() => { setDialogType('assessment'); setOpen(true); }}
                onCreateCpciAssessment={() => { setDialogType('cpci_assessment'); setOpen(true); }}
            />

            {/* Tutorial A — Independent classes nudge popup */}
            {isLevel21 && showClassesNudge && (
                <ClassesIndependentNudge onDismiss={handleDismissClassesNudge} />
            )}

            {/* Tutorial B — Active chat nudge banner */}
            {isLevel21 && showChatBanner && (
                <ChatNudgeBanner
                    authUser={authUser}
                    history={history}
                    onDismiss={handleDismissChatBanner}
                />
            )}

            <Dialog
                open={open}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        border: '2px solid #000000',
                        borderRadius: '15px',
                        overflow: {xs: 'scroll', sm: 'hidden'},
                    }
                }}
            >
                {(dialogType === 'assessment' || dialogType === 'cpci_assessment') ? (
                    <Box sx={{p: 4, position: 'relative'}}>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{position: 'absolute', right: 8, top: 8, color: '#999'}}
                        >
                            <CloseIcon/>
                        </IconButton>
                        <Typography variant="h5" sx={{mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold'}}>
                            {dialogType === 'cpci_assessment'
                                ? 'Create Your Own CPC Index Assessment'
                                : 'Create Your Own AI Readiness Assessment'}
                        </Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={4} md={4} sx={{mx: {sm: 'auto'}}}>
                                <Box sx={{height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <img src={AccessInfoIcon} alt="assessment" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}/>
                                </Box>
                                <Button
                                    className={classes.popupButton}
                                    variant="contained"
                                    sx={{width: '100%', mt: 4}}
                                    onClick={() => {
                                        handleCloseDialog();
                                        history.push(dialogType === 'cpci_assessment'
                                            ? '/dq_pro/products/cpci/assessments'
                                            : '/dq_pro/products/assessments');
                                    }}
                                >
                                    Create Now
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                {dialogType === 'cpci_assessment' ? (
                                    <>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Child Protection &amp; Competency Index:</strong> Measure how ready and protected young people are across the CPC Index competencies.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Reports for All Stakeholders:</strong> Individual scorecards, parent reports, and school dashboards to monitor progress and risk levels.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Evidence-Based Decisions:</strong> Help your clients confidently inform curriculum development and policy-making with solid, actionable data.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Comprehensive by default:</strong> Every CPC Index indicator for the selected target audience is included automatically.
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Clear Insights:</strong> Help your learners rapidly identify their strengths and areas needing improvement in AI readiness with individual scorecards.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Reports for All Stakeholders:</strong> Parent reports and school dashboards to monitor progress and levels of AI readiness.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Evidence-Based Decisions:</strong> Enable your clients to confidently inform curriculum development and policy-making with solid, actionable data.
                                        </Typography>
                                        <Typography sx={{mb: 2}}>
                                            <strong>Curate to your target audience and needs:</strong> Select from a list of indicators that you want to assess for any demographic.
                                        </Typography>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                ) : showExistingConfirmation ? (
                    <Box sx={{p: 4, position: 'relative'}}>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{position: 'absolute', right: 8, top: 8, color: '#999'}}
                        >
                            <CloseIcon/>
                        </IconButton>
                        <Typography variant="h6" sx={{mt: 2, mb: 4, textAlign: 'center', color: '#ff5757'}}>
                            You already have an existing program in progress. <br/>Do you want to continue with the existing program or start a new program?
                        </Typography>
                        <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 4}}>
                            <Button
                                className={classes.popupButton}
                                variant="contained"
                                sx={{width: '30%', fontSize: '15px !important'}}
                                onClick={() => { handleCloseDialog(); history.push('/dq_pro/program/creation/preview'); }}
                            >
                                Continue
                            </Button>
                            <Button
                                className={classes.popupButton}
                                variant="outlined"
                                sx={{width: '30%', borderColor: '#ff5757', color: '#ff5757', fontWeight: 'bold', fontSize: '15px !important'}}
                                onClick={() => { handleCloseDialog(); handleStartNew(); }}
                            >
                                Start New Program
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{p: 4, position: 'relative'}}>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{position: 'absolute', right: 8, top: 8, color: '#999'}}
                        >
                            <CloseIcon/>
                        </IconButton>
                        <Typography variant="h5" sx={{mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold'}}>
                            Powerful Curriculum and AI/Digital Literacy Program Creation
                        </Typography>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={4} md={4} sx={{mx: {sm: 'auto'}}}>
                                <Box sx={{height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <img src={ProgramInfoIcon} alt="program" style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}/>
                                </Box>
                                <Button
                                    className={classes.popupButton}
                                    variant="contained"
                                    sx={{width: '100%', mt: 4}}
                                    onClick={() => {
                                        if (hasExistingProgram) {
                                            setShowExistingConfirmation(true);
                                        } else {
                                            handleStartNew();
                                        }
                                    }}
                                >
                                    Build a Program
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography sx={{mb: 2}}>
                                    <strong>Structured Curricula for Any Group:</strong> Generate age-appropriate learning programs organized into clear modules, units, and lessons—perfect for schools, organizations, or community groups.
                                </Typography>
                                <Typography sx={{mb: 2}}>
                                    <strong>Backed by 100+ International Standards and Frameworks:</strong> All content is high-quality, curated by experts, and backed by 100+ international standards and frameworks to ensure accuracy, relevance, and impact.
                                </Typography>
                                <Typography sx={{mb: 2}}>
                                    <strong>Media-Rich and Engaging:</strong> Every lesson comes with designed slides with images, videos, and interactive activities to make learning fun and visually engaging.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Dialog>
        </>
    )
}

export default DQProHome;
